import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { IconButton, Skeleton, Tab, Tabs, Tooltip } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";

import breakupClasses from "../../Component/BrandIndexHistory/BrandIndexBreakups.module.css";
import AverageSentimentScores from "../../Component/BrandIndexHistory/AverageSentimentScores";
import BaseResponsesModal from "../../Component/BaseResponseComponent/BaseResponsesModal";
import useBaseResponsesModal from "../../Component/BaseResponseComponent/useBaseResponsesModal";
import ErrorView from "../../Component/ErrorView/ErrorView";
import Modal from "../../Component/Modal/Modal";
import MUIIconButton from "../../Component/UI/MUIIconButton/MUIIconButton";
import MUISelect from "../../Component/UI/MUISelect/MUISelect";
import MUIMultiSelect from "../../Component/UI/MUIMultiSelect/MUIMultiSelect";
import Alert from "../../Component/UI/Alert/Alert";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { LLM_OPTIONS, OVERVIEW_LLM_LABELS } from "../../utils/constant";
import { formatSectionLabel } from "../../utils/helpers";

import AttributesOverview from "../BrandAudit/CompAttributes/CompAttributes";
import CategoryDimensions from "../BrandAudit/CategoryDimensions/CategoryDimensions";
import CoreAttributes from "../BrandAudit/CoreAttributes/CoreAttributes";
import Share from "../BrandAudit/Share/Share";
import SourcesOverview from "../BrandAudit/Sources/sourcesOverview";
import { filterKey } from "../BrandAudit/BrandAudit";

import { CategoryAnalysisContext } from "./category-analysis-context";
import { ALL_METRIC_VALUES } from "./metricOptions";
import CategoryScoreCards from "./CategoryScoreCards";
import CAVisibilityRankings from "./CAVisibilityRankings";
import CAVisibilityRankingsComposite from "./CAVisibilityRankingsComposite";
import CACorePillarsComposite from "./CACorePillarsComposite";
import CAPurchaseCriteriaComposite from "./CAPurchaseCriteriaComposite";
import CAPositiveNegativeComposite from "./CAPositiveNegativeComposite";
import HTMLReportTab from "../BrandAudit/HTMLReportTab/HTMLReportTab";
import classes from "../BrandAudit/BrandScore/BrandScore.module.css";
import dashClasses from "./CategoryDashboard.module.css";
import { toast } from "react-toastify";

function formatAllBrandsPNContent(baseResponsesDataArray, iswebSearchLLM) {
  if (!Array.isArray(baseResponsesDataArray) || baseResponsesDataArray.length === 0) {
    return "No data available.";
  }

  const formatBaseResults = (results) => {
    if (!results || !Array.isArray(results)) return "";
    const count = results.length;
    return results
      .map((item, idx) => {
        const text = typeof item === "string" ? item : (item.baseRes || "");
        return count > 1 ? `**Response ${idx + 1}:**\n\n${text}` : text;
      })
      .join("\n\n---\n\n");
  };

  let content = "";

  baseResponsesDataArray.forEach((competitor, idx) => {
    const brandName = competitor.brandName || `Competitor ${idx + 1}`;
    const baseResponse = competitor.baseResponses || {};

    if (content) content += `\n\n${"=".repeat(60)}\n\n`;

    if (iswebSearchLLM) {
      if (baseResponse.positiveAttributePrompt) {
        content += `## Query 1: Positive Attributes\n\n`;
        content += `### Base Prompt for ${brandName}\n\n${baseResponse.positiveAttributePrompt}\n\n`;
        const positiveContent = formatBaseResults(baseResponse.positiveBaseResults);
        if (positiveContent) content += `---\n\n### Base Responses\n\n${positiveContent}`;
      }
      if (baseResponse.negativeAttributePrompt) {
        content += `\n\n---\n\n## Query 2: Negative Attributes\n\n`;
        content += `### Base Prompt for ${brandName}\n\n${baseResponse.negativeAttributePrompt}\n\n`;
        const negativeContent = formatBaseResults(baseResponse.negativeBaseResults);
        if (negativeContent) content += `---\n\n### Base Responses\n\n${negativeContent}`;
      }
    } else {
      const baseResArray = Array.isArray(baseResponse.baseRes) ? baseResponse.baseRes : baseResponse;
      if (baseResponse.basePrompt) {
        content += `### Base Prompt for ${brandName}\n\n${baseResponse.basePrompt}\n\n`;
      }
      const baseResContent = formatBaseResults(baseResArray);
      if (baseResContent) content += `---\n\n### Base Responses\n\n${baseResContent}`;
    }
  });

  return content || "No data available.";
}

/**
 * The main CategoryDashboard component.
 * Renders the active category tab based on selectedCategory from context.
 */
export default function CategoryDashboard({
  categories,
  market,
  llms,
  competitors,
  enableHTMLReport = false,
  transactionId,
  brandName = "",
  markets = [],
  personas = [],
}) {
  const caCtx = useContext(CategoryAnalysisContext);
  const [showReportingTab, setShowReportingTab] = useState(false);

  if (!categories?.length || !llms?.length) return null;

  const activeCategory = caCtx.state.selectedCategory || categories[0];
  const activeCategoryIndex = Math.max(0, categories.indexOf(activeCategory));
  const reportingTabIndex = categories.length;
  const isReportingTabActive = enableHTMLReport && showReportingTab;
  const currentTabValue = isReportingTabActive ? reportingTabIndex : activeCategoryIndex;
  const showTabs = categories.length > 1 || enableHTMLReport;

  const handleTabChange = (_, idx) => {
    if (enableHTMLReport && idx === reportingTabIndex) {
      setShowReportingTab(true);
    } else {
      setShowReportingTab(false);
      caCtx.updateSelectedCategory(categories[idx]);
    }
  };

  return (
    <div>
      {showTabs && (
        <Tabs
          value={currentTabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            borderBottom: "1px solid #e0e6f0",
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "0.875rem" },
            "& .Mui-selected": { color: "#1976d2" },
          }}
        >
          {categories.map((cat) => (
            <Tab key={cat} label={cat} />
          ))}
          {enableHTMLReport && <Tab key="reporting" label="Reporting" />}
        </Tabs>
      )}

      {!isReportingTabActive && (
        <CategoryAnalysisCategoryTab
          key={activeCategory}
          category={activeCategory}
          index={activeCategoryIndex}
          market={market}
          llms={llms}
          competitors={competitors}
        />
      )}

      {isReportingTabActive && (
        <HTMLReportTab
          brandName={brandName}
          categories={categories}
          llms={llms}
          markets={markets}
          personas={personas}
          transactionId={transactionId}
          flag="categoryAnalysis"
        />
      )}
    </div>
  );
}

function CategoryAnalysisCategoryTab({
  category,
  index,
  market,
  llms,
  competitors,
}) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [selectedBrandsForPN, setSelectedBrandsForPN] = useState(null);
  const discoveryCtx = useContext(CategoryAnalysisContext);
  const { user } = useContext(AuthContext);
  const isCompanyUser = user?.user_role?.role?.name === "Company user";

  const entity = discoveryCtx.entity || category;
  const selectedLLM = discoveryCtx.state.selectedLLM;
  const transactionStatus = discoveryCtx.state.transactionStatus;
  const selectedMarket = discoveryCtx.state?.selectedMarket;
  const selectedPersona = discoveryCtx.state?.selectedPersona;
  const primaryLLM = discoveryCtx.primary_llm;
  const primaryPersona = discoveryCtx?.persona[0]?.title ?? discoveryCtx?.persona[0];
  const selectedCompetitor = discoveryCtx?.state?.selectedCompetitor;
  const isCombinationAllowed = discoveryCtx.isCombinationAllowed;
  const selectedMetrics = discoveryCtx.state?.selectedMetrics?.length > 0
    ? discoveryCtx.state.selectedMetrics
    : ALL_METRIC_VALUES;

  // Compare Across state (synced from CategoryScoreCards via context)
  const compareMode = discoveryCtx.compareMode || "default";
  const multiLLMs = discoveryCtx.multiLLMs?.length > 0 ? discoveryCtx.multiLLMs : (discoveryCtx.llm || []);
  const isCompareLLM = compareMode === "llm" && multiLLMs.length > 1;

  const prevCombinationAllowedRef = useRef(true);
  useEffect(() => {
    if (prevCombinationAllowedRef.current !== isCombinationAllowed) {
      if (!isCombinationAllowed) {
        toast.warn("Combination not available");
      }
      prevCombinationAllowedRef.current = isCombinationAllowed;
    }
  }, [isCombinationAllowed]);

  // Global filter options — drive all sections below the header
  const allLLMs = discoveryCtx.llm || [];
  const allMarkets = discoveryCtx.markets || [];
  const allPersonas = (discoveryCtx?.persona || [])
    .map(p => (typeof p === "object" && p?.title ? p.title : typeof p === "string" ? p : null))
    .filter(Boolean);
  const llmOptions = allLLMs.map(l => ({ name: OVERVIEW_LLM_LABELS[l] || l, value: l }));
  const marketOptions = allMarkets.map(m => ({ name: m === "GeneralMarket" ? "General Market" : m, value: m }));
  const personaOptions = allPersonas.map(p => ({ name: p, value: p }));

  // Brand Pillars and Purchase Criteria are only run for the primary market
  const isPrimaryMarket = !selectedMarket || selectedMarket === discoveryCtx.market;

  const competitorsName = discoveryCtx?.competitors?.[category]?.map((c) => c.name) ?? [];

  // CA-specific rank score data for Visibility Data section
  const caRankScoreExtras = discoveryCtx.rank_scores[selectedLLM]?.[category]?.[selectedMarket]?.[selectedPersona]?.extras;
  const caProductMappingAttribute = caRankScoreExtras?.productMappingAttribute ?? {};
  const caProductRankingsArray = caRankScoreExtras?.productRankingsArray ?? [];
  const caProductRankingData = caRankScoreExtras?.productRankingsData ?? [];
  const caRankMatchesIndices = caRankScoreExtras?.rankMatchesIndices ?? [];
  const attributeKeys = Object.keys(caProductMappingAttribute);
  const effectiveAttribute = (selectedAttribute && attributeKeys.includes(selectedAttribute))
    ? selectedAttribute
    : (attributeKeys[0] ?? null);
  const attributeOptions = attributeKeys.map((key) => ({
    name: formatSectionLabel(key),
    value: key,
  }));

  let hasError = false;
  let loading = true;


  const data =
    discoveryCtx.brand_scores[selectedLLM]?.[category]?.[selectedMarket]?.[selectedPersona]?.[selectedCompetitor]
      ?.extras;

  if (data?.sentimentMeta && data?.sentiementKeyDrivers) {
    loading = false;
  } else if (transactionStatus === "RETRY") {
    hasError = true;
    loading = false;
  } else if (transactionStatus === "RUNNING") {
    hasError = false;
    loading = true;
  }

  // Rank Data has its own data source (rank_scores / composite_rank_score) — its loading/error
  // state must not be derived from sentiment fields, otherwise deselecting the Sentiment metric
  // (or it simply resolving later than Rank) leaves the Rank Data section stuck on skeleton forever.
  const hasRankData =
    caProductRankingsArray.length > 0 ||
    Object.keys(discoveryCtx.composite_rank_score?.[category]?.[selectedMarket]?.[selectedPersona] ?? {}).length > 0;

  let rankHasError = false;
  let rankLoading = true;
  if (hasRankData) {
    rankLoading = false;
  } else if (transactionStatus === "RETRY") {
    rankHasError = true;
    rankLoading = false;
  } else if (transactionStatus === "RUNNING") {
    rankHasError = false;
    rankLoading = true;
  } else {
    // Transaction isn't running/retrying and there's still no rank data (e.g. Rank wasn't selected) — stop loading.
    rankLoading = false;
  }

  const {
    infoModalOpen: categoryInfoModalOpen,
    infoModalTitle: categoryInfoModalTitle,
    infoModalContent: categoryInfoModalContent,
    closeInfoModal: closeCategoryInfoModal,
    handleInfoClick,
  } = useBaseResponsesModal(data, "visibility");

  const {
    infoModalOpen: pnInfoModalOpen,
    infoModalTitle: pnInfoModalTitle,
    infoModalContent: pnInfoModalContent,
    closeInfoModal: closePnInfoModal,
    handleSignalStrengthClick: handlePnSignalStrengthClick,
  } = useBaseResponsesModal({}, "signal-strength");

  const [pnCompareInfoOpen, setPnCompareInfoOpen] = useState(false);
  const [pnCompareInfoTabs, setPnCompareInfoTabs] = useState([]);

  const pdfRef = useRef(null);

  function LoadingSkeletion() {
    return (
      <div className="p-3">
        <Skeleton variant="rectangular" height={30} />
        {Array(10)
          .fill(0)
          .map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width={i % 2 === 0 ? "70%" : "97%"}
              className="my-3"
              height={20}
            />
          ))}
      </div>
    );
  }

  // --- Section: Category Score Cards ---
  const categoryScoreCardsSection = (
    <CategoryScoreCards category={category} />
  );

  // --- Section: Visibility Data (CA-specific: attribute-based single card, no brand highlight) ---
  const caReachData = (() => {
    if (!caProductRankingsArray.length || !effectiveAttribute) return null;
    const indices = caProductMappingAttribute[effectiveAttribute] ?? [];
    if (!indices.length) return null;
    // Compute sectionIndices offset based on position of effectiveAttribute among all keys
    let offset = 0;
    for (const key of attributeKeys) {
      if (key === effectiveAttribute) break;
      offset += (caProductMappingAttribute[key]?.length ?? 0);
    }
    const sectionIndices = indices.map((_, i) => offset + i);
    return (
      <div data-report-section="reach" style={{ width: "100%" }}>
        <CAVisibilityRankings
          rankings={indices.map((i) => caProductRankingsArray[i])}
          productRankingDataSets={indices.map((i) => caProductRankingData[i] ?? null)}
          pIndices={indices}
          sectionIndices={sectionIndices}
        />
      </div>
    );
  })();

  // --- Section: Average Sentiment Scores ---
  // In composite (By LLM) mode we check any LLM has sentimentMeta instead of only the selected one
  const anySentimentAvailable = isCompareLLM
    ? multiLLMs.some(llm =>
        discoveryCtx.brand_scores[llm]?.[category]?.[selectedMarket]?.[selectedPersona]?.some(
          b => b?.extras?.sentimentMeta?.length > 0 || b?.extras?.favorabilities?.length > 0
        )
      )
    : (data?.sentimentMeta);

  const averageSentimentScores = (
    <div style={{ paddingTop: 12 }}>
      {anySentimentAvailable && !loading && !hasError && (
        <AverageSentimentScores
          category={category}
          market={selectedMarket}
          selectedPersona={selectedPersona}
          selectedLLM={selectedLLM}
          competitors={competitors}
          ctx={discoveryCtx}
          compareMode={compareMode}
          multiLLMs={multiLLMs}
        />
      )}
      {hasError && <div className={classes.error}><ErrorView title="Error Fetching Sentiment Data" message="Request Failed" /></div>}
      {loading && !hasError && <LoadingSkeletion />}
    </div>
  );

  // --- Section: Positive/Negative Attributes ---
  const effectivePNBrands = selectedBrandsForPN ?? competitorsName.slice(0, 5);
  const pnBrandSummary = effectivePNBrands.length === competitorsName.length
    ? "All Brands"
    : effectivePNBrands.length === 1
      ? "1 Brand Selected"
      : `${effectivePNBrands.length} Brands Selected`;

  const pnPrimaryMarket = discoveryCtx.markets?.[0];

  function getPNBaseResponsesForLLM(llm) {
    let ref = discoveryCtx.positive_negative_attributes?.[llm]?.[category]?.[pnPrimaryMarket]?.[selectedPersona];
    if (Array.isArray(ref)) {
      ref = ref.filter(c => effectivePNBrands.includes(c.name));
    }
    return Array.isArray(ref)
      ? ref
          .filter(competitor => competitor?.baseResponses && Object.keys(competitor.baseResponses).length > 0)
          .map(competitor => ({ brandName: competitor.name, baseResponses: competitor.baseResponses }))
      : [];
  }

  const pnIsWebSearchLLM = selectedLLM?.toLowerCase()?.includes("search");
  const pnAllBaseResponsesData = getPNBaseResponsesForLLM(selectedLLM);
  const pnHasBaseResponses = isCompareLLM
    ? multiLLMs.some(llm => getPNBaseResponsesForLLM(llm).length > 0)
    : pnAllBaseResponsesData.length > 0;

  function handleComparePNInfoClick() {
    const tabs = multiLLMs.map((llm) => ({
      label: OVERVIEW_LLM_LABELS[llm] || llm,
      content: formatAllBrandsPNContent(getPNBaseResponsesForLLM(llm), llm?.toLowerCase()?.includes("search")),
    }));
    setPnCompareInfoTabs(tabs);
    setPnCompareInfoOpen(true);
  }

  const positiveNegativeAttributes = (
    <div id={`${category}_positivenegative_attributes`}>
      {competitors[category]?.length > 0 && (
        isCompareLLM ? (
          <CAPositiveNegativeComposite
            category={category}
            market={selectedMarket}
            persona={selectedPersona}
            multiLLMs={multiLLMs}
            filterBrands={effectivePNBrands}
            ctx={discoveryCtx}
          />
        ) : (
          <AttributesOverview
            category={category}
            isCategoryOverview={true}
            ctx={discoveryCtx}
            filterBrands={effectivePNBrands}
          />
        )
      )}
    </div>
  );

  // --- Section: Brand Pillars (CoreAttributes for brand type) ---
  // In "By LLM" compare mode, show composite averaged view across all selected LLMs
  const brandPillars = (
    <div className={classes.section_summary} style={{ paddingTop: 10, paddingBottom: 10 }}>
      <div className={classes.flex_one}>
        {competitors[category]?.length > 0 && (
          isCompareLLM ? (
            <CACorePillarsComposite
              category={category}
              entity={entity}
              ctx={discoveryCtx}
              multiLLMs={multiLLMs}
            />
          ) : (
            <CoreAttributes category={category} entity={entity} ctx={discoveryCtx} />
          )
        )}
      </div>
    </div>
  );

  // --- Section: Purchase Criteria (CategoryDimensions) ---
  // In "By LLM" compare mode, show composite averaged view with attribute name normalisation
  const purchaseCriteria = (
    <div className={classes.section_summary} style={{ paddingTop: 10, paddingBottom: 10 }}>
      <div className={classes.flex_one}>
        {competitors[category]?.length > 0 && (
          isCompareLLM ? (
            <CAPurchaseCriteriaComposite
              category={category}
              entity={entity}
              ctx={discoveryCtx}
              multiLLMs={multiLLMs}
            />
          ) : (
             <CategoryDimensions category={category} entity={entity} ctx={discoveryCtx} module="categoryAnalysis" />
          )
        )}
      </div>
    </div>
  );


  // --- Visibility Data section (CA: attribute-based, no competitor dropdown) ---
  const VisibilityDataSection = () => {
    const visibilityTitle = discoveryCtx?.mapMetricTitle?.("Rank Data") ?? "Rank Data";
    const attrIndices = effectiveAttribute ? (caProductMappingAttribute[effectiveAttribute] ?? []) : [];

    // BE composite rank data from socket (type "compositeRank")
    const compositeRankData = isCompareLLM
      ? discoveryCtx.composite_rank_score?.[category]?.[selectedMarket]?.[selectedPersona]
      : null;

    // Attribute options: prefer BE composite attribute keys, then any LLM's rank_scores keys
    const compositeAttrKeys = isCompareLLM
      ? (() => {
          // 1. BE composite data has its own productMappingAttribute
          const beKeys = Object.keys(compositeRankData?.productMappingAttribute ?? {});
          if (beKeys.length > 0) return beKeys;
          // 2. Fall back to any LLM's rank_scores attribute keys
          for (const llm of multiLLMs) {
            const keys = Object.keys(
              discoveryCtx.rank_scores?.[llm]?.[category]?.[selectedMarket]?.[selectedPersona]?.extras?.productMappingAttribute ?? {}
            );
            if (keys.length > 0) return keys;
          }
          return attributeKeys;
        })()
      : attributeKeys;

    const compositeAttrOptions = isCompareLLM
      ? compositeAttrKeys.map((key) => ({ name: formatSectionLabel(key), value: key }))
      : attributeOptions;

    const compositeEffectiveAttr = isCompareLLM
      ? ((selectedAttribute && compositeAttrKeys.includes(selectedAttribute))
          ? selectedAttribute
          : (compositeAttrKeys[0] ?? null))
      : effectiveAttribute;

    // Compare-by-LLM mode: each LLM has its own base prompt/responses, so build a per-LLM
    // tabbed view instead of the single-LLM `handleInfoClick` (which is bound to one LLM's data).
    const [compareInfoOpen, setCompareInfoOpen] = useState(false);
    const [compareInfoTabs, setCompareInfoTabs] = useState([]);

    function buildLLMVisibilityContent(llm) {
      const extras = discoveryCtx.rank_scores?.[llm]?.[category]?.[selectedMarket]?.[selectedPersona]?.extras;
      const mapping = extras?.productMappingAttribute ?? {};
      const indices = compositeEffectiveAttr ? (mapping[compositeEffectiveAttr] ?? []) : [];
      const baseResponses = extras?.baseResponses ?? [];
      const items = indices.map((i) => baseResponses[i]).filter(Boolean);
      if (!items.length) return "No data available for this attribute.";

      const firstPrompt = items[0]?.basePrompt;
      const allSamePrompt = items.every((it) => it?.basePrompt === firstPrompt);

      if (allSamePrompt) {
        let content = firstPrompt ? `### Base Prompt\n\n${firstPrompt}` : "";
        const responses = items
          .filter((it) => it?.baseResponse)
          .map((it, idx) => `**Response ${idx + 1}:**\n\n${it.baseResponse}`)
          .join("\n\n---\n\n");
        if (responses) {
          content += content ? `\n\n---\n\n### Responses\n\n${responses}` : `### Responses\n\n${responses}`;
        }
        return content || "No data available for this attribute.";
      }

      return items
        .map((it, idx) => `### Query ${idx + 1}\n\n**Base Prompt:**\n\n${it?.basePrompt || ""}\n\n---\n\n**Response:**\n\n${it?.baseResponse || ""}`)
        .join("\n\n---\n\n");
    }

    function handleCompareVisibilityInfoClick() {
      const tabs = multiLLMs.map((llm) => ({
        label: OVERVIEW_LLM_LABELS[llm] || llm,
        content: buildLLMVisibilityContent(llm),
      }));
      setCompareInfoTabs(tabs);
      setCompareInfoOpen(true);
    }

    return (
      <div>
        <div className={dashClasses.sectionHeadingRow}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <h5 className={dashClasses.sectionHeading}>{visibilityTitle}</h5>
            {/* Base prompt info icon — single-LLM data in default view, per-LLM tabs when comparing */}
            {!isCompanyUser && (isCompareLLM ? compositeEffectiveAttr : effectiveAttribute) && (
              <Tooltip title="View Base Prompt" arrow>
                <IconButton
                  size="small"
                  onClick={() => isCompareLLM ? handleCompareVisibilityInfoClick() : handleInfoClick(effectiveAttribute, attrIndices)}
                  sx={{ padding: "4px", "&:hover": { backgroundColor: "primary.light", color: "primary.main" } }}
                >
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            {(isCompareLLM ? compositeAttrOptions : attributeOptions).length > 0 && (
              <MUISelect
                fullWidth={false}
                optionValue={isCompareLLM ? compositeEffectiveAttr : effectiveAttribute}
                label="Attribute"
                options={isCompareLLM ? compositeAttrOptions : attributeOptions}
                onChange={(e) => setSelectedAttribute(e.target.value)}
              />
            )}
          </div>
        </div>
        <div className={classes.printSpacing}>
          {rankLoading && !rankHasError ? (
            <LoadingSkeletion />
          ) : isCompareLLM ? (
            <CAVisibilityRankingsComposite
              effectiveAttribute={compositeEffectiveAttr}
              compositeData={compositeRankData || null}
            />
          ) : (
            caReachData
          )}
        </div>
        <BaseResponsesModal
          open={compareInfoOpen}
          title={`${visibilityTitle} - Base Prompt and Responses`}
          tabs={compareInfoTabs}
          onClose={() => setCompareInfoOpen(false)}
        />
      </div>
    );
  };

  // --- Section wrapper (replaces per-section MUICard) ---
  const SectionBlock = ({ id: sectionId, title: sectionTitle, componentOutput, children, headerExtra }) => (
    <div>
      <div className={dashClasses.sectionHeadingRow}>
        <h5 className={dashClasses.sectionHeading}>{sectionTitle}</h5>
        {headerExtra}
      </div>
      <div id={sectionId}>{componentOutput || children}</div>
    </div>
  );

  // --- Category header with global filter bar ---
  const insightsSection = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: "0 0 12px", fontWeight: 700, fontSize: "1.3rem", color: "#1a1a2e" }}>{category}</h3>
        <div className={dashClasses.globalFiltersBar}>
          {/* View toggle: Single View (single LLM) vs By LLM (composite across all LLMs) */}
          {allLLMs.length > 1 && (
            <MUISelect
              label="View"
              fullWidth={false}
              optionValue={compareMode === "llm" ? "llm" : "default"}
              options={[
                { name: "Single View", value: "default" },
                { name: "By LLM (Compare All)", value: "llm" },
              ]}
              onChange={e => {
                const mode = e.target.value;
                discoveryCtx.updateCompareMode(mode);
                discoveryCtx.updateMultiLLMs(mode === "llm" ? allLLMs : []);
              }}
            />
          )}
          {/* LLM select only in Single View — By LLM compares all LLMs automatically */}
          {compareMode !== "llm" && llmOptions.length > 0 && (
            <MUISelect
              label="LLM"
              fullWidth={false}
              optionValue={selectedLLM}
              options={llmOptions}
              onChange={e => discoveryCtx.updateSelectedLLM(e.target.value)}
            />
          )}
          {marketOptions.length > 0 && (
            <MUISelect
              label="Market"
              fullWidth={false}
              optionValue={selectedMarket}
              options={marketOptions}
              onChange={e => discoveryCtx.updateSelectedMarket(e.target.value)}
            />
          )}
          {personaOptions.length > 0 && (
            <MUISelect
              label="Persona"
              fullWidth={false}
              optionValue={selectedPersona}
              options={personaOptions}
              onChange={e => discoveryCtx.updateSelectedPersona(e.target.value)}
            />
          )}

          {/* Current-selection summary chips — right-aligned inside the same filter box */}
          <div className={dashClasses.ctxSummary}>
            {[
              {
                key: "AI Model",
                icon: (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                    <path d="M9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" />
                  </svg>
                ),
                value: compareMode === "llm" ? "All LLMs" : (OVERVIEW_LLM_LABELS[selectedLLM] || selectedLLM),
                comparing: compareMode === "llm",
              },
              {
                key: "Market",
                icon: (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9" />
                    <path d="M3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
                  </svg>
                ),
                value: selectedMarket === "GeneralMarket" ? "General Market" : selectedMarket,
              },
              {
                key: "Persona",
                icon: (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                ),
                value: selectedPersona,
              },
            ].map(dim => (
              <div key={dim.key} className={`${dashClasses.ctxSummaryRow} ${dim.comparing ? dashClasses.ctxSummaryRowActive : ""}`}>
                <span className={dashClasses.ctxSummaryIcon}>{dim.icon}</span>
                <span className={dashClasses.ctxSummaryKey}>{dim.key}</span>
                <span className={dashClasses.ctxSummarySep} />
                <span className={dashClasses.ctxSummaryVal}>
                  {dim.value || "—"}
                  {dim.comparing && <span className={dashClasses.ctxSummaryBadge}>COMPARING</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Share — far right, outside the filter box */}
      <MUIIconButton title="Click to Share" onClick={() => setShowShareModal(true)}>
        <ShareOutlinedIcon color="primary" />
      </MUIIconButton>
    </div>
  );

  return (
    <div key={entity + category + "section"}>
      <div className={dashClasses.globalCard}>

        {/* Category header */}
        <div className={dashClasses.cardHeaderSection}>
          {insightsSection}
        </div>

        <div className={dashClasses.sectionDivider} />

        {/* Category Analysis Score Cards */}
        <div className={`${dashClasses.sectionContainer} ${classes.printSpacing}`}>
          {categoryScoreCardsSection}
        </div>

        <div className={dashClasses.sectionDivider} />

        {/* Visibility Data: only when Rank metric was run */}
        {selectedMetrics.includes("rank") && (
          <>
            <div className={dashClasses.sectionContainer}>
              <VisibilityDataSection />
            </div>

            <div className={dashClasses.sectionDivider} />
            <div className={classes.pageBreak} />
          </>
        )}

        {/* Average Sentiment Scores: only when Sentiment metric was run */}
        {selectedMetrics.includes("sentiment") && (
          <>
            <div className={dashClasses.sectionContainer}>
              <SectionBlock
                id="average_sentiment_scores"
                title={discoveryCtx?.mapMetricTitle?.("Average Sentiment Scores") ?? "Average Sentiment Scores"}
                componentOutput={averageSentimentScores}
              />
            </div>

            <div className={dashClasses.sectionDivider} />
            <div className={classes.pageBreak} />
          </>
        )}

        {/* Positive / Negative Attributes: only available for the primary market and when Signal-Strength metric was run */}
        {isPrimaryMarket && selectedMetrics.includes("signalStrength") && (
          <>
            <div className={dashClasses.sectionContainer}>
              <SectionBlock
                id="positiveNegativeAttributes"
                title={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                    Positive and Negative Attributes
                    {!isCompanyUser && pnHasBaseResponses && (
                      <Tooltip title="View Base Prompt" arrow placement="top">
                        <IconButton
                          size="small"
                          onClick={() => isCompareLLM
                            ? handleComparePNInfoClick()
                            : handlePnSignalStrengthClick(pnIsWebSearchLLM, pnAllBaseResponsesData, discoveryCtx.type || "brand")}
                          sx={{
                            padding: "2px",
                            color: "rgba(0, 0, 0, 0.54)",
                            "&:hover": {
                              backgroundColor: "rgba(33, 150, 243, 0.08)",
                              color: "#2196F3",
                            },
                          }}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </span>
                }
                componentOutput={positiveNegativeAttributes}
                headerExtra={competitorsName.length > 0 && (
                  <div style={{ minWidth: 180 }}>
                    <MUIMultiSelect
                      label="Brands"
                      options={competitorsName.map(n => ({ name: n, value: n }))}
                      value={effectivePNBrands}
                      onChange={(vals) => setSelectedBrandsForPN(vals.length === 0 ? null : vals)}
                      shouldShowValue={false}
                      placeholder={pnBrandSummary}
                    />
                  </div>
                )}
              />
            </div>

            <div className={dashClasses.sectionDivider} />
            <div className={classes.pageBreak} />
          </>
        )}

        {/* Brand Pillars: only available for the primary market and when Core-Attribute metric was run */}
        {isPrimaryMarket && selectedMetrics.includes("coreAttribute") && (
          <>
            <div className={`${dashClasses.sectionContainer} ${classes.printSpacing}`}>
              <SectionBlock
                id="Brand_Piller"
                title={ENTITY_LABELS.brandPillar}
                componentOutput={brandPillars}
              />
            </div>

            <div className={dashClasses.sectionDivider} />
          </>
        )}

        {/* Purchase Criteria: only available for the primary market and when Category-Dimension metric was run */}
        {isPrimaryMarket && selectedMetrics.includes("categoryDimension") && (
          <>
            <div className={dashClasses.sectionContainer}>
              <SectionBlock
                id="purchase_criteria"
                title={ENTITY_LABELS.purchaseCriteria}
                componentOutput={purchaseCriteria}
              />
            </div>

            <div className={dashClasses.sectionDivider} />
            <div className={classes.pageBreak} />
          </>
        )}

        {/* Sources */}
        <div className={`${dashClasses.sectionContainer} ${dashClasses.sourcesHeadingOverride}`}>
          <SourcesOverview
            category={category}
            isCategoryOverview={false}
            ctx={discoveryCtx}
          />
        </div>

      </div>

      {/* Modals rendered outside the card */}
      <BaseResponsesModal
        open={categoryInfoModalOpen}
        title={categoryInfoModalTitle}
        content={categoryInfoModalContent}
        onClose={closeCategoryInfoModal}
      />

      <BaseResponsesModal
        open={pnInfoModalOpen}
        title={pnInfoModalTitle}
        content={pnInfoModalContent}
        onClose={closePnInfoModal}
      />

      <BaseResponsesModal
        open={pnCompareInfoOpen}
        title="Positive and Negative Attributes - All Brands"
        tabs={pnCompareInfoTabs}
        onClose={() => setPnCompareInfoOpen(false)}
      />

      <Modal
        height="85vh"
        width="700"
        title="Share Link"
        open={showShareModal}
        handleClose={() => setShowShareModal(false)}
      >
        <Share setShowShareModal={setShowShareModal} ctx={discoveryCtx} moduleName="Category Analysis" showEntityLabel={false} />
      </Modal>
    </div>
  );
}
